import React, { FC, useMemo, useState } from 'react';
import { Translate } from 'react-localize-redux';
// @ts-ignore
import SimpleLineIcons from 'react-simple-line-icons';
import { PulseLoader } from 'react-spinners';
import { Alert, Button } from 'reactstrap';

import useTranslate from '../../../hooks/useTranslate';
import { MappedOrderItemType, MappedOrderItemUserType } from '../../../services/apis/order/types';
import {
  isCardNrAlreadyAdded,
  validateCardNumber,
  validateCardNumberFormat,
} from '../services/validation';
import { FriendType } from './types';

interface ValidationError {
  friendId: string | number;
  message: string;
}

interface FriendsProps {
  item?: MappedOrderItemType;
  players: MappedOrderItemUserType[];
  friends: FriendType[];
  flightFull?: boolean;
  onFriendAddClick: (friend: FriendType) => void;
  isGolfhaftetRequest: boolean;
}

export const Friends: FC<FriendsProps> = ({
  item,
  players,
  friends,
  flightFull = false,
  onFriendAddClick,
  isGolfhaftetRequest,
}) => {
  const { translate } = useTranslate();

  const [isValidating, setIsValidating] = useState<string | number | null>(null);
  const [error, setError] = useState<ValidationError | null>(null);

  // Exclude friends already in players
  const friendList = useMemo(() => {
    return friends.filter(friend => {
      const possibleKeys = ['id', 'email', 'golfId', 'ghCardNr'] as const;
      // Use type assertion to tell TypeScript that friend has these properties
      const typedFriend = friend as unknown as Record<
        (typeof possibleKeys)[number],
        string | number | undefined
      >;
      const keys = possibleKeys.filter(key => Boolean(typedFriend[key])) as Array<
        keyof Pick<MappedOrderItemUserType, 'id' | 'email' | 'golfId' | 'ghCardNr'>
      >;

      return !players.some(player =>
        keys.some(
          key =>
            typedFriend[key] ===
            player[
              key as keyof Pick<MappedOrderItemUserType, 'id' | 'email' | 'golfId' | 'ghCardNr'>
            ]
        )
      );
    });
  }, [friends, players]);

  if (friendList.length === 0) return null;

  // Validate and add friend if valid
  const onFriendClick = async (friend: FriendType): Promise<void> => {
    if (isGolfhaftetRequest) {
      const valid = await isValidGolfhaftetCard(friend);
      if (!valid) return;
    }

    onFriendAddClick(friend);
  };

  // Check a GM card’s validity
  const isValidGolfhaftetCard = async (friend: FriendType): Promise<boolean> => {
    // Use type assertion to tell TypeScript that friend has these properties
    const typedFriend = friend as FriendType & { id?: string | number };
    const friendId = typedFriend.id ?? friend.key;
    if (!friendId || !item) return false;

    setError(null);

    const cardNr = typedFriend.ghCardNr;
    if (!cardNr) return false;

    let validationError = validateCardNumberFormat(cardNr);
    if (validationError) {
      setError({ friendId, message: String(translate(validationError)) });
      return false;
    }

    validationError = isCardNrAlreadyAdded(players, cardNr);
    if (validationError) {
      setError({ friendId, message: String(translate(validationError)) });
      return false;
    }

    setIsValidating(friendId);
    validationError = await validateCardNumber(
      cardNr,
      item.golfCourseId,
      typeof item.startTime === 'number'
        ? new Date(item.startTime).toISOString()
        : item.startTime.toISOString()
    );
    setIsValidating(null);

    if (validationError) {
      setError({ friendId, message: String(translate(validationError)) });
      return false;
    }

    return true;
  };

  return (
    <>
      <h4 className="mt-3 mb-2 fw-bold">
        <Translate id="default.AddFriend" />
      </h4>

      {friendList.map(friend => {
        // Use type assertion to tell TypeScript that friend has these properties
        const typedFriend = friend as FriendType & {
          id?: string | number;
          firstName?: string;
          lastName?: string;
          email?: string;
          golfId?: string;
        };
        const friendId = typedFriend.id ?? friend.key;
        const label =
          (typedFriend.firstName && typedFriend.lastName
            ? `${typedFriend.firstName} ${typedFriend.lastName}`
            : '') ||
          typedFriend.email ||
          typedFriend.golfId ||
          '';
        const isLoading = isValidating === friendId;
        const showError = error?.friendId === friendId;

        return (
          <div key={friendId} className="mb-2">
            <Button
              className="border d-flex align-items-center justify-content-between px-3 py-2 text-black"
              block
              color="link"
              disabled={flightFull || Boolean(isValidating)}
              onClick={() => onFriendClick(friend)}
              style={{ borderRadius: '8px' }}
            >
              <div className="d-flex align-items-center gap-2">
                <SimpleLineIcons size="medium" name="plus" />
                <span>
                  {label}
                  {isGolfhaftetRequest && typedFriend.ghCardNr && ` - ${typedFriend.ghCardNr}`}
                </span>
              </div>
              {isLoading && <PulseLoader size={10} />}
            </Button>

            {showError && (
              <Alert color="danger" className="mt-1 py-1">
                {error!.message}
              </Alert>
            )}
          </div>
        );
      })}
    </>
  );
};
